/**
 * Signed-in shell: header, the main lobby (home screen) or current game, chat, and notices.
 * The header's logo and name show only in a game on wide screens (the main lobby shows its own).
 * Phones: the header tools are icons, and in a game they sit in the table's top row (no top bar
 * at all). The Tutorial (Learn to Play: a scripted one-round game, `learn:start`) starts from the main lobby
 * here, and from Options outside a game. A new account is first asked 'Have you played Ka'Dice before?'
 * (`Welcome`), over everything; No starts Learn to Play. In Learn to Play the table is in Portrait Layout
 * with Beginner Mode on and Shake & Flick dice (where the screen can show 3D) whatever your options; the
 * dice tray's note can switch to Popple or Classic, which is saved as your Dice Type. Started from the
 * first login, Shake & Flick is saved as your Dice Type too. Leaving it is back to the main lobby.
 * Also the recap / replay of a finished game (`Review`), and the links
 * `/join/<id>` and `/replay/<id>` (see `App`), cleared from the address bar once handled.
 */
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { AVATAR_DEFAULTS, AVATAR_MAX_BYTES, AVATAR_SIZE_PX, DISPLAY_NAME_MAX_LENGTH, t } from '@kadice/shared';
import { api } from '../api';
import { setMusicWanted } from '../audio';
import { useFullscreen } from '../fullscreen';
import { applyDice, useOptions, type DiceType, type Options } from '../options';
import { canShow3d } from '../dice3d/support';
import { disablePush, resyncPush, useTabAlert } from '../turnAlerts';
import { usePhone } from '../usePhone';
import { useGameClient, type GameClient } from '../useGameClient';
import { AdminTools } from './AdminTools';
import { DiceLab } from './DiceLab';
import { Avatar } from './Avatar';
import { Chat } from './Chat';
import { FeedbackModal, captureScreen } from './FeedbackModal';
import { GameTable } from './GameTable';
import { Leaderboard } from './Leaderboard';
import { LobbyList } from './LobbyList';
import { Notices } from './Notices';
import { OptionsMenu } from './OptionsMenu';
import { Review, type ReviewMode } from './Review';
import { Welcome } from './Welcome';

/** A link into the game (see `App`): an invite to a game, or a game's replay. */
export interface DeepLink {
  kind: 'join' | 'replay';
  gameId: number;
}

/** Done with a deep link: back to the plain address, so a reload doesn't act on it again. */
const clearLink = () => history.replaceState(null, '', '/');

/**
 * An invite link: once connected (and the lobby list is in, when not in a game), join the lobby,
 * or watch the game if it has started. Once only.
 */
function useJoinLink(client: GameClient, gameId: number | null) {
  const done = useRef(false);
  const { connection, me, view, listed, lobbies, request, notify } = client;
  useEffect(() => {
    if (gameId === null || done.current || connection !== 'online' || !me) return;
    if (!view && !listed) return;
    done.current = true;
    clearLink();
    if (view?.gameId === gameId) return;
    const entry = lobbies.find((l) => l.gameId === gameId);
    if (!view && !entry) return notify({ kind: 'error', text: t('error.inviteGone') });
    void request(entry?.inProgress ? 'lobby:watch' : 'lobby:join', { gameId });
  }, [gameId, connection, me, view, listed, lobbies, request, notify]);
}

export function Main({
  onSignedOut,
  isAdmin,
  askPlayedBefore,
  link,
}: {
  onSignedOut: () => void;
  isAdmin: boolean;
  /** A new account's first login: ask 'Have you played Ka'Dice before?' before anything else. */
  askPlayedBefore: boolean;
  link: DeepLink | null;
}) {
  const client = useGameClient(onSignedOut);
  useJoinLink(client, link?.kind === 'join' ? link.gameId : null);
  // A finished game's recap or replay; a replay link opens straight into it.
  const [review, setReview] = useState<{ gameId: number; mode: ReviewMode } | null>(
    link?.kind === 'replay' ? { gameId: link.gameId, mode: 'replay' } : null,
  );
  const openReview = (gameId: number, mode: ReviewMode) => setReview({ gameId, mode });
  const closeReview = () => {
    if (link?.kind === 'replay') clearLink();
    setReview(null);
  };
  const [options, setOptions] = useOptions();
  useFullscreen(options.fullscreen, () => setOptions({ fullscreen: false }));
  const [showOptions, setShowOptions] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showDiceLab, setShowDiceLab] = useState(false);
  const [asking, setAsking] = useState(askPlayedBefore);
  // Learn to Play: asked for, sent once connected (the first login may answer before the socket is up).
  const [wantLearn, setWantLearn] = useState(false);
  const { connection, request } = client;
  useEffect(() => {
    if (!wantLearn || connection !== 'online') return;
    void request('learn:start').finally(() => setWantLearn(false));
  }, [wantLearn, connection, request]);
  // Saved to the account; if that fails it is simply asked again next time.
  const answer = (playedBefore: boolean) => {
    setAsking(false);
    if (!playedBefore) {
      // A new player keeps the tutorial's Shake & Flick dice.
      if (canShow3d()) setOptions({ diceType: 'motion' });
      setWantLearn(true);
    }
    void api.setPlayedBefore(playedBefore).catch(() => {});
  };
  // Learn to Play: its table settings, whatever your options (the dice note's pick is saved).
  const learning = !!client.view?.learn;
  const [learnDice, setLearnDice] = useState<DiceType | null>(null);
  if (!learning && learnDice !== null) setLearnDice(null);
  const tableOptions: Options = learning
    ? {
        ...options,
        layout: 'portrait',
        beginnerMode: true,
        highlightAttackable: true,
        autoNext: false,
        debug: false,
        diceType: learnDice ?? 'motion',
      }
    : options;
  applyDice(tableOptions);
  const pickLearnDice = (diceType: DiceType) => {
    setLearnDice(diceType);
    setOptions({ diceType });
  };
  // Leaderboard modal: closed, or the tab it opens on.
  const [stats, setStats] = useState<'total' | 'mine' | null>(null);
  // undefined: closed. Otherwise the screenshot taken as it opened (null if capture failed).
  const [feedback, setFeedback] = useState<string | null | undefined>(undefined);
  const [capturing, setCapturing] = useState(false);
  const phone = usePhone();
  useTabAlert(!!client.view?.you.awaited);
  useEffect(() => void resyncPush(), []);
  // Lobby music: the main lobby and a game's lobby, until it starts (not under the first-login question or tutorial).
  const musicWanted = (!client.view || client.view.phase === 'lobby') && !asking;
  useEffect(() => setMusicWanted(musicWanted), [musicWanted]);
  useEffect(() => () => setMusicWanted(false), []);

  const openFeedback = async () => {
    setCapturing(true);
    const shot = await captureScreen();
    setCapturing(false);
    setFeedback(shot);
  };

  const signOut = async () => {
    await disablePush(); // this browser's alerts belong to the player signing out
    await api.signOut().catch(() => {});
    onSignedOut();
  };

  // Phones: no top bar in a game; the tools ride in the table's top row as icons.
  const tools = (
    <HeaderTools
      client={client}
      isAdmin={isAdmin}
      compact={phone}
      capturing={capturing}
      onFeedback={() => void openFeedback()}
      onOptions={() => setShowOptions(true)}
      onLeaderboard={() => setStats('total')}
      onAdmin={() => setShowAdmin(true)}
    />
  );
  const inGame = !!client.view;

  return (
    <div className={`shell${phone && inGame ? ' no-topbar' : ''}`}>
      {!(phone && inGame) && (
        <header className="topbar">
          {!phone && inGame && <img src="/img/logo.png" alt={t('app.title')} className="logo small" />}
          {!phone && inGame && <span className="me-name">{client.me?.displayName}</span>}
          <span className="spacer" />
          {tools}
        </header>
      )}
      <main className="content">
        {client.view ? (
          <GameTable
            client={client}
            view={client.view}
            options={tableOptions}
            tools={phone ? tools : undefined}
            onReview={openReview}
            onDiceType={learning ? pickLearnDice : undefined}
          />
        ) : (
          <LobbyList
            client={client}
            onReview={openReview}
            playStyle={options.playStyle}
            onPlayStyle={(playStyle) => setOptions({ playStyle })}
            onTutorial={() => setWantLearn(true)}
            onStats={() => setStats('mine')}
          />
        )}
      </main>
      <aside className="sidebar">
        <Chat client={client} showDebug={options.debug && options.debugBotReasons} />
      </aside>
      <Notices notices={client.notices} client={client} showDebug={options.debug && options.debugBotReasons} />
      {feedback !== undefined && (
        <FeedbackModal client={client} options={options} screenshot={feedback} onClose={() => setFeedback(undefined)} />
      )}
      {showOptions && (
        <OptionsMenu
          options={options}
          onChange={setOptions}
          onClose={() => setShowOptions(false)}
          nameEditor={<NameEditor client={client} />}
          avatarEditor={<AvatarEditor client={client} />}
          onSignOut={() => void signOut()}
          onStats={() => {
            setShowOptions(false);
            setStats('mine');
          }}
          onTutorial={
            inGame
              ? undefined
              : () => {
                  setShowOptions(false);
                  setWantLearn(true);
                }
          }
        />
      )}
      {asking && <Welcome onAnswer={answer} />}
      {showAdmin && (
        <AdminTools
          onClose={() => setShowAdmin(false)}
          onDiceLab={() => {
            setShowAdmin(false);
            setShowDiceLab(true);
          }}
        />
      )}
      {showDiceLab && (
        <DiceLab name={client.me?.displayName ?? ''} haptics={options.haptics} onClose={() => setShowDiceLab(false)} />
      )}
      {stats && <Leaderboard me={client.me?.id} initialTab={stats} onClose={() => setStats(null)} />}
      {review && (
        <Review
          client={client}
          gameId={review.gameId}
          mode={review.mode}
          onMode={(mode) => setReview({ ...review, mode })}
          onClose={closeReview}
        />
      )}
    </div>
  );
}

/** Connection state, Admin tools (admins), Leaderboard, Feedback, Options (Sign out lives there). `compact`: icons only (phones). */
function HeaderTools({
  client,
  isAdmin,
  compact,
  capturing,
  onFeedback,
  onOptions,
  onLeaderboard,
  onAdmin,
}: {
  client: GameClient;
  isAdmin: boolean;
  compact: boolean;
  capturing: boolean;
  onFeedback: () => void;
  onOptions: () => void;
  onLeaderboard: () => void;
  onAdmin: () => void;
}) {
  const label = (icon: string, text: string) => (compact ? icon : text);
  const cls = compact ? 'icon-btn' : undefined;
  return (
    <span className={`header-tools${compact ? ' compact' : ''}`}>
      {client.connection !== 'online' && <span className="badge warn">{t(`connection.${client.connection}`)}</span>}
      {isAdmin && (
        <button className={cls} onClick={onAdmin} title={t('header.adminHint')} aria-label={t('header.admin')}>
          {label('🛠️', t('header.admin'))}
        </button>
      )}
      <button
        className={cls}
        onClick={onLeaderboard}
        title={t('header.leaderboardHint')}
        aria-label={t('header.leaderboard')}
      >
        {label('🏆', t('header.leaderboard'))}
      </button>
      <button
        className={cls}
        onClick={onFeedback}
        disabled={capturing}
        title={t('header.feedbackHint')}
        aria-label={t('header.feedback')}
      >
        {label(capturing ? '⏳' : '💬', t(capturing ? 'header.capturing' : 'header.feedback'))}
      </button>
      <button className={cls} onClick={onOptions} title={t('header.options')} aria-label={t('header.options')}>
        {label('⚙️', t('header.options'))}
      </button>
    </span>
  );
}

/** Your display name, edited in the Options menu. */
function NameEditor({ client }: { client: GameClient }) {
  const [draft, setDraft] = useState<string | null>(null);
  if (!client.me) return null;

  if (draft === null) {
    return (
      <div className="row">
        <span>{client.me.displayName}</span>
        <button title={t('header.renameHint')} onClick={() => setDraft(client.me!.displayName)}>
          {t('options.rename')}
        </button>
      </div>
    );
  }
  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      client.setMe(await api.rename(draft));
      setDraft(null);
    } catch (err) {
      client.notify({ kind: 'error', text: err instanceof Error ? err.message : t('header.renameFailed') });
    }
  };

  return (
    <form className="inline" onSubmit={(e) => void save(e)}>
      <input value={draft} maxLength={DISPLAY_NAME_MAX_LENGTH} autoFocus onChange={(e) => setDraft(e.target.value)} />
      <button type="submit">{t('common.save')}</button>
      <button type="button" onClick={() => setDraft(null)}>
        {t('common.cancel')}
      </button>
    </form>
  );
}

/** A picked picture, centre-cropped to a square and shrunk to AVATAR_SIZE_PX, as a data URL. */
async function squareAvatar(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = Math.min(side, AVATAR_SIZE_PX);
  const sx = (bitmap.width - side) / 2;
  const sy = (bitmap.height - side) / 2;
  canvas.getContext('2d')!.drawImage(bitmap, sx, sy, side, side, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL('image/webp', 0.9); // browsers without WebP encoding give PNG, also accepted
}

/** Your avatar, edited in the Options menu: pick a default or upload a picture. */
function AvatarEditor({ client }: { client: GameClient }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  if (!client.me) return null;
  const meId = client.me.id;

  const fail = (err: unknown) =>
    client.notify({ kind: 'error', text: err instanceof Error ? err.message : t('options.avatarFailed') });
  const change = async (send: () => Promise<void>) => {
    setBusy(true);
    try {
      await send(); // the server's avatar:changed refreshes the picture
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > AVATAR_MAX_BYTES) return fail(new Error(t('error.avatarTooBig')));
    void change(async () => api.setAvatar({ image: await squareAvatar(file) }));
  };

  return (
    <div className="avatar-editor">
      <Avatar playerId={meId} className="large" />
      <div className="avatar-defaults">
        {Array.from({ length: AVATAR_DEFAULTS }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            className="avatar-choice"
            disabled={busy}
            onClick={() => void change(() => api.setAvatar({ default: n }))}
            aria-label={t('options.avatarDefault', { n })}
            title={t('options.avatarDefault', { n })}
          >
            <img src={`/img/avatars/${n}.svg`} alt="" className="avatar" />
          </button>
        ))}
      </div>
      <button disabled={busy} onClick={() => fileRef.current?.click()} title={t('options.avatarUploadHint')}>
        {t('options.avatarUpload')}
      </button>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onFile} />
      <small className="hint">{t('options.avatarUploadHint')}</small>
    </div>
  );
}
